import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../api/client'
import type { components } from '../api/schema'

/**
 * Data collection campaigns (spec 2026-10-20-12): one hook per endpoint. The
 * manager creates, launches, follows and closes; the agent fills unit by unit.
 */

export type Campaign = components['schemas']['Campaign']
export type CampaignSummary = components['schemas']['CampaignSummary']
export type CampaignInput = components['schemas']['CampaignInput']
export type CampaignGroupingInput = components['schemas']['CampaignGroupingInput']
export type CampaignItemInput = components['schemas']['CampaignItemInput']
export type CampaignItem = components['schemas']['CampaignItem']
export type CampaignUnit = components['schemas']['CampaignUnit']
export type CampaignUnitForm = components['schemas']['CampaignUnitForm']
export type CampaignEntry = components['schemas']['CampaignEntry']
export type CampaignChange = components['schemas']['CampaignChange']
export type CampaignProgress = components['schemas']['CampaignProgressResponse']['progress']
export type CampaignPropertyOption = components['schemas']['CampaignPropertyOption']
export type CampaignStatus = components['schemas']['CampaignStatus']
export type CampaignLevel = components['schemas']['CampaignLevel']

/** An API error, with the field messages of a 422 and its code. */
export class CampaignApiError extends Error {
    code?: string
    fields: Array<{ name: string; message: string }>

    constructor(message: string, code?: string, fields: Array<{ name: string; message: string }> = []) {
        super(message)
        this.code = code
        this.fields = fields
    }
}

function fail(error: unknown): never {
    const e = error as { title?: string; code?: string; fields?: Array<{ name: string; message: string }> } | undefined
    throw new CampaignApiError(e?.fields?.[0]?.message || e?.title || 'Request failed', e?.code, e?.fields ?? [])
}

const keys = {
    all: (org: string) => ['campaigns', org] as const,
    list: (org: string) => ['campaigns', org, 'list'] as const,
    one: (org: string, uid: string) => ['campaigns', org, 'one', uid] as const,
    units: (org: string, uid: string) => ['campaigns', org, 'units', uid] as const,
    unit: (org: string, uid: string, unitUid: string) => ['campaigns', org, 'unit', uid, unitUid] as const,
    progress: (org: string, uid: string) => ['campaigns', org, 'progress', uid] as const,
    changes: (org: string, uid: string) => ['campaigns', org, 'changes', uid] as const,
    options: (org: string, code: string) => ['campaigns', org, 'options', code] as const,
}

function useInvalidate(org: string) {
    const qc = useQueryClient()
    return () => qc.invalidateQueries({ queryKey: keys.all(org) })
}

export function useCampaigns(org: string) {
    return useQuery({
        queryKey: keys.list(org),
        queryFn: async (): Promise<CampaignSummary[]> => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/campaigns', {
                params: { path: { orgCode: org } },
            })
            if (error) fail(error)
            return data?.campaigns ?? []
        },
    })
}

export function useCampaign(org: string, uid: string) {
    return useQuery({
        queryKey: keys.one(org, uid),
        queryFn: async (): Promise<Campaign> => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/campaigns/{campaignUid}', {
                params: { path: { orgCode: org, campaignUid: uid } },
            })
            if (error || !data) fail(error)
            return data.campaign
        },
    })
}

export function useCampaignPropertyOptions(org: string, assetTypeCode: string | undefined) {
    return useQuery({
        queryKey: keys.options(org, assetTypeCode ?? ''),
        enabled: !!assetTypeCode,
        queryFn: async (): Promise<CampaignPropertyOption[]> => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/campaigns/property-options', {
                params: { path: { orgCode: org }, query: { asset_type_code: assetTypeCode } },
            })
            if (error) fail(error)
            return data?.properties ?? []
        },
    })
}

export function useCreateCampaign(org: string) {
    const invalidate = useInvalidate(org)
    return useMutation({
        mutationFn: async (body: CampaignInput): Promise<Campaign> => {
            const { data, error } = await apiClient.POST('/api/v1/organizations/{orgCode}/campaigns', {
                params: { path: { orgCode: org } },
                body,
            })
            if (error || !data) fail(error)
            return data.campaign
        },
        onSuccess: invalidate,
    })
}

export function useUpdateCampaign(org: string, uid: string) {
    const invalidate = useInvalidate(org)
    return useMutation({
        mutationFn: async (body: CampaignInput): Promise<Campaign> => {
            const { data, error } = await apiClient.PATCH('/api/v1/organizations/{orgCode}/campaigns/{campaignUid}', {
                params: { path: { orgCode: org, campaignUid: uid } },
                body,
            })
            if (error || !data) fail(error)
            return data.campaign
        },
        onSuccess: invalidate,
    })
}

export function useDeleteCampaign(org: string, uid: string) {
    const invalidate = useInvalidate(org)
    return useMutation({
        mutationFn: async () => {
            const { error } = await apiClient.DELETE('/api/v1/organizations/{orgCode}/campaigns/{campaignUid}', {
                params: { path: { orgCode: org, campaignUid: uid } },
            })
            if (error) fail(error)
        },
        onSuccess: invalidate,
    })
}

export function useLaunchCampaign(org: string, uid: string) {
    const invalidate = useInvalidate(org)
    return useMutation({
        mutationFn: async (): Promise<Campaign> => {
            const { data, error } = await apiClient.POST('/api/v1/organizations/{orgCode}/campaigns/{campaignUid}/launch', {
                params: { path: { orgCode: org, campaignUid: uid } },
            })
            if (error || !data) fail(error)
            return data.campaign
        },
        onSuccess: invalidate,
    })
}

export function useCloseCampaign(org: string, uid: string) {
    const invalidate = useInvalidate(org)
    return useMutation({
        mutationFn: async (): Promise<Campaign> => {
            const { data, error } = await apiClient.POST('/api/v1/organizations/{orgCode}/campaigns/{campaignUid}/close', {
                params: { path: { orgCode: org, campaignUid: uid } },
            })
            if (error || !data) fail(error)
            return data.campaign
        },
        onSuccess: invalidate,
    })
}

export function useCampaignProgress(org: string, uid: string, enabled: boolean) {
    return useQuery({
        queryKey: keys.progress(org, uid),
        enabled,
        queryFn: async (): Promise<CampaignProgress> => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/campaigns/{campaignUid}/progress', {
                params: { path: { orgCode: org, campaignUid: uid } },
            })
            if (error || !data) fail(error)
            return data.progress
        },
    })
}

export function useCampaignChanges(org: string, uid: string, enabled: boolean) {
    return useQuery({
        queryKey: keys.changes(org, uid),
        enabled,
        queryFn: async (): Promise<CampaignChange[]> => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/campaigns/{campaignUid}/changes', {
                params: { path: { orgCode: org, campaignUid: uid } },
            })
            if (error) fail(error)
            return data?.changes ?? []
        },
    })
}

export function useDecideChange(org: string, uid: string) {
    const invalidate = useInvalidate(org)
    return useMutation({
        mutationFn: async (args: { entryUid: string; decision: 'accept' | 'reject' }): Promise<CampaignChange> => {
            const { data, error } = await apiClient.POST(
                '/api/v1/organizations/{orgCode}/campaigns/{campaignUid}/entries/{entryUid}/decision',
                { params: { path: { orgCode: org, campaignUid: uid, entryUid: args.entryUid } }, body: { decision: args.decision } },
            )
            if (error || !data) fail(error)
            return data.change
        },
        onSuccess: invalidate,
    })
}

export function useRetryFailed(org: string, uid: string) {
    const invalidate = useInvalidate(org)
    return useMutation({
        mutationFn: async (): Promise<number> => {
            const { data, error } = await apiClient.POST('/api/v1/organizations/{orgCode}/campaigns/{campaignUid}/retry', {
                params: { path: { orgCode: org, campaignUid: uid } },
            })
            if (error || !data) fail(error)
            return data.requeued
        },
        onSuccess: invalidate,
    })
}

export function useCampaignUnits(org: string, uid: string) {
    return useQuery({
        queryKey: keys.units(org, uid),
        queryFn: async (): Promise<CampaignUnit[]> => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/campaigns/{campaignUid}/units', {
                params: { path: { orgCode: org, campaignUid: uid } },
            })
            if (error) fail(error)
            return data?.units ?? []
        },
    })
}

export function useCampaignUnit(org: string, uid: string, unitUid: string) {
    return useQuery({
        queryKey: keys.unit(org, uid, unitUid),
        queryFn: async (): Promise<CampaignUnitForm> => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/campaigns/{campaignUid}/units/{unitUid}', {
                params: { path: { orgCode: org, campaignUid: uid, unitUid } },
            })
            if (error || !data) fail(error)
            return data.unit
        },
    })
}

export function useAnswerEntry(org: string, uid: string) {
    const invalidate = useInvalidate(org)
    return useMutation({
        mutationFn: async (args: { entryUid: string; answer: unknown; confirmed: boolean }): Promise<CampaignEntry> => {
            const { data, error } = await apiClient.PUT('/api/v1/organizations/{orgCode}/campaigns/{campaignUid}/entries/{entryUid}', {
                params: { path: { orgCode: org, campaignUid: uid, entryUid: args.entryUid } },
                body: { answer: args.answer, confirmed: args.confirmed },
            })
            if (error || !data) fail(error)
            return data.entry
        },
        onSuccess: invalidate,
    })
}

export function useCompleteUnit(org: string, uid: string, unitUid: string) {
    const invalidate = useInvalidate(org)
    return useMutation({
        mutationFn: async (force: boolean): Promise<CampaignUnit> => {
            const { data, error } = await apiClient.POST(
                '/api/v1/organizations/{orgCode}/campaigns/{campaignUid}/units/{unitUid}/complete',
                { params: { path: { orgCode: org, campaignUid: uid, unitUid } }, body: { force } },
            )
            if (error || !data) fail(error)
            return data.unit
        },
        onSuccess: invalidate,
    })
}

/** Downloads the .xlsx of every answer of the campaign. */
export async function downloadCampaignExport(org: string, uid: string, name: string) {
    const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/campaigns/{campaignUid}/export', {
        params: { path: { orgCode: org, campaignUid: uid } },
        parseAs: 'blob',
    })
    if (error || !data) fail(error)
    const url = URL.createObjectURL(data as Blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${name || 'campaign'}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
}
